import { apiClient } from './client';
import { isApiError } from './errors';
import type { components } from './generated/app-api';

/**
 * Типизированный клиент App API (app-api-service, FRONTEND_GUIDE §6).
 * Типы — из OpenAPI (generated/app-api.ts); пути и заголовки — по гайду.
 */

type Schemas = components['schemas'];

export type Money = Schemas['MoneyOut'];
export type ChessboardUnit = Schemas['ChessboardUnitOut'];
export type Chessboard = Schemas['ChessboardResponse'];
export type Approval = Schemas['ApprovalOut'];
export type ApprovalsResponse = Schemas['ApprovalsResponse'];
export type ObjectConfig = Schemas['ObjectConfigOut'];
export type ObjectConfigInput = Partial<Schemas['ObjectConfigIn']>;
export type ManualPriceInput = Schemas['ManualPriceIn'];
export type ManualPriceResult = Schemas['ManualPriceResultOut'];

/** Статусы помещения (§4 Enums) */
export const UNIT_STATUSES = ['available', 'held', 'booked', 'sold', 'withdrawn'] as const;
export type UnitStatus = (typeof UNIT_STATUSES)[number];

/** Типы помещения (§4 Enums) */
export const UNIT_TYPES = ['apartment', 'parking', 'storage', 'commercial', 'other'] as const;
export type UnitType = (typeof UNIT_TYPES)[number];

/** Статусы подтверждения (§4 Enums) */
export type ApprovalStatus = 'awaiting_approval' | 'pending' | 'rejected';

export interface HealthStatus {
  status: string;
  backend: 'postgres' | 'memory' | string;
  auth: 'oidc' | 'disabled' | string;
}

const encode = encodeURIComponent;

export const appApi = {
  /** GET /healthz — без аутентификации (§2) */
  health: () => apiClient.get<HealthStatus>('/healthz'),

  /** GET /objects/{project}/chessboard (§6.1) */
  getChessboard: (project: string, signal?: AbortSignal) =>
    apiClient.get<Chessboard>(`/objects/${encode(project)}/chessboard`, { signal }),

  /** GET /approvals?project= (§6.2) */
  listApprovals: (project?: string, signal?: AbortSignal) =>
    apiClient.get<ApprovalsResponse>('/approvals', { query: { project }, signal }),

  /** POST /approvals/{id}/approve (§6.3) */
  approve: (approvalId: number) =>
    apiClient.post<Approval>(`/approvals/${encode(String(approvalId))}/approve`),

  /** POST /approvals/{id}/reject (§6.4) */
  reject: (approvalId: number) =>
    apiClient.post<Approval>(`/approvals/${encode(String(approvalId))}/reject`),

  /** GET /objects/{project}/config (§6.5): 404 = конфига ещё нет → null */
  getObjectConfig: async (project: string, signal?: AbortSignal): Promise<ObjectConfig | null> => {
    try {
      return await apiClient.get<ObjectConfig>(`/objects/${encode(project)}/config`, { signal });
    } catch (error) {
      if (isApiError(error) && error.status === 404) return null;
      throw error;
    }
  },

  /** PUT /objects/{project}/config — upsert (§6.6) */
  putObjectConfig: (project: string, input: ObjectConfigInput) =>
    apiClient.put<ObjectConfig>(`/objects/${encode(project)}/config`, input),

  /** POST /objects/{project}/units/{unit}/price (§6.7); actor → X-Actor */
  setUnitPrice: (project: string, unit: string, input: ManualPriceInput, actor?: string) =>
    apiClient.post<ManualPriceResult>(
      `/objects/${encode(project)}/units/${encode(unit)}/price`,
      input,
      actor ? { headers: { 'X-Actor': actor } } : undefined,
    ),
};
